import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { RiMagicLine, RiFileTextLine, RiMailLine, RiArticleLine } from 'react-icons/ri';
import GlassButton from '../../components/ui/v2/GlassButton';
import ResumeSelection from '../../components/resumes/ResumeSelection';
import JobDescriptionInput from '../../components/resumes/JobDescriptionInput';
import GenerationRules from '../../components/resumes/GenerationRules';
import { useProfile } from '../../context/ProfileContext';
import { DEFAULT_GENERATION_RULES, type GenerationRules as RulesModel } from '../../models/generation';
import { resumeService } from '../../services/resumeService';
import { creditService } from '../../services/creditService';
import { coverLetterService } from '../../services/coverLetterService';

import { notify } from '../../lib/notifications';
import { ApiException } from '../../lib/api/errors';
import DocumentHistory from '../../components/resumes/DocumentHistory';

const SAMPLE_JOB_DESCRIPTION = `Job Title: Senior Frontend Engineer
Company: Nebulon Tech (San Francisco, CA / Remote)

About Us:
Nebulon Tech is a Series B startup revolutionizing the way teams collaborate in 3D space. We are building the next generation of spatial computing tools for the web.

Key Responsibilities:
• Architect and build scalable, high-performance web applications using React, TypeScript, and WebGL.
• Collaborate closely with product designers and backend engineers to deliver seamless user experiences.
• Optimize application performance for complex 3D rendering and real-time collaboration features.
• Mentor junior engineers and establish best practices for frontend development.
• Participate in code reviews and technical design discussions.

Requirements:
• 5+ years of professional experience in frontend development.
• Strong proficiency in JavaScript/TypeScript, HTML5, and CSS3.
• Deep understanding of React and its ecosystem (Redux, Context API, Hooks).
• Experience with 3D graphics libraries (Three.js, Babylon.js) is a strong plus.
• Familiarity with modern build tools (Webpack, Vite) and CI/CD pipelines.
• Excellent problem-solving skills and attention to detail.

Bonus Points:
• Experience with WebAssembly or WebGPU.
• Contributions to open-source projects.
• Background in game development or interactive media.`;


export default function Resumes() {
    const { data: profileData, isLoading } = useProfile();
    const navigate = useNavigate();

    // State
    const [viewMode, setViewMode] = useState<'create' | 'history'>('create');
    const [selectedResumeId, setSelectedResumeId] = useState<string>('');

    // Job Input State
    const [jobInputMode, setJobInputMode] = useState<'text' | 'url'>('text');
    const [jobDescription, setJobDescription] = useState('');
    const [jobUrl, setJobUrl] = useState('');

    const [isGenerating, setIsGenerating] = useState(false);

    // History State
    const [resumeHistory, setResumeHistory] = useState<any[]>([]);
    const [coverLetterHistory, setCoverLetterHistory] = useState<any[]>([]);
    const [historyLoading, setHistoryLoading] = useState(false);

    // Configuration State
    const [genConfig, setGenConfig] = useState<RulesModel>(DEFAULT_GENERATION_RULES);

    // Sync generation type helper
    const setGenerationType = (type: 'resume' | 'cover_letter') => {
        setGenConfig(prev => ({ ...prev, document_type: type }));
    };

    // Select default resume on load
    useEffect(() => {
        if (profileData?.resumes && profileData.resumes.length > 0 && !selectedResumeId) {
            const defaultResume = profileData.resumes.find(r => Number(r.is_default)) || profileData.resumes[0];
            setSelectedResumeId(defaultResume.id);
        }
    }, [profileData, selectedResumeId]);

    // Fetch history when entering history mode
    useEffect(() => {
        if (viewMode === 'history') {
            const fetchHistory = async () => {
                setHistoryLoading(true);
                try {
                    const [resumes, coverLetters] = await Promise.all([
                        resumeService.getGeneratedResumes(),
                        coverLetterService.getCoverLetters()
                    ]);

                    // Helper to extract company from JD
                    const getCompanyFromJD = (jd: string) => {
                        if (!jd) return '';
                        const match = jd.match(/Company:\s*(.*?)(\n|$)/i);
                        return match ? match[1].trim() : '';
                    };

                    // Map to common format
                    setResumeHistory(resumes.map(r => ({
                        id: r.id,
                        title: r.name || 'Untitled Resume',
                        subtitle: getCompanyFromJD(r['job_description'] as string) || 'Generated Resume',
                        date: r.created_at,
                        type: 'resume'
                    })));

                    setCoverLetterHistory(coverLetters.map(c => {
                        const company = c.company_name || getCompanyFromJD(c.job_description);
                        return {
                            id: c.id,
                            title: company ? `Cover Letter for ${company}` : 'Untitled Cover Letter',
                            subtitle: c.job_description?.substring(0, 100).replace(/\n/g, ' ') + '...',
                            date: c.created_at,
                            type: 'cover_letter'
                        };
                    }));
                } catch (err) {
                    console.error("Failed to fetch history:", err);
                } finally {
                    setHistoryLoading(false);
                }
            };
            fetchHistory();
        }
    }, [viewMode]);

    const handleGenerate = async () => {
        if (!selectedResumeId) return;

        setIsGenerating(true);
        try {
            // Prepare input based on mode
            const jobInput = {
                description: jobInputMode === 'text' ? jobDescription : undefined,
                url: jobInputMode === 'url' ? jobUrl : undefined
            };

            // Basic validation
            if (!jobInput.description && !jobInput.url) {
                // TODO: Show toast
                setIsGenerating(false);
                return;
            }

            if (genConfig.document_type === 'cover_letter') {
                const payload: any = {
                    resume_id: selectedResumeId,
                };
                if (jobInput.url) payload.job_url = jobInput.url;
                else payload.job_description = jobInput.description;

                const result = await coverLetterService.generateCoverLetter(payload);
                creditService.invalidate();
                navigate(`/app/cover-letters/${result.cover_letter_id}/editor`);
            } else {
                const result = await resumeService.generateResume(
                    jobInput,
                    selectedResumeId,
                    genConfig
                );
                creditService.invalidate();
                navigate(`/app/resumes/${result.generated_resume_id}/editor`);
            }
        } catch (error) {
            console.error(error);
            if (error instanceof ApiException) {
                notify.error(error.message);
            } else {
                notify.error('An unexpected error occurred. Please try again.');
            }
        } finally {
            setIsGenerating(false);
        }
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-[600px]">
                <div className="w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div className="space-y-6 max-w-[1600px] mx-auto pb-20 animate-fade-in">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold text-white mb-2">Resume & Cover Letter Generator</h1>
                    <p className="text-gray-400">
                        Create tailored documents or view your history
                    </p>
                </div>

                {/* View Switcher */}
                <div className="flex p-1 bg-white/5 rounded-lg border border-white/5">
                    <button
                        onClick={() => setViewMode('create')}
                        className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${viewMode === 'create' ? 'bg-brand-500 text-white shadow-lg' : 'text-gray-400 hover:text-white'}`}
                    >
                        Create New
                    </button>
                    <button
                        onClick={() => setViewMode('history')}
                        className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${viewMode === 'history' ? 'bg-brand-500 text-white shadow-lg' : 'text-gray-400 hover:text-white'}`}
                    >
                        History
                    </button>
                </div>
            </div>

            {viewMode === 'create' ? (
                <>
                    {/* Main Grid */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 h-[600px]">
                        {/* Left Panel: Resume Selection */}
                        <div className="h-full">
                            <ResumeSelection
                                resumes={profileData?.resumes || []}
                                selectedId={selectedResumeId}
                                onSelect={setSelectedResumeId}
                            />
                        </div>

                        {/* Right Panel: Job Description */}
                        <div className="h-full">
                            <JobDescriptionInput
                                mode={jobInputMode}
                                onModeChange={setJobInputMode}
                                textValue={jobDescription}
                                onTextChange={setJobDescription}
                                urlValue={jobUrl}
                                onUrlChange={setJobUrl}
                                onUseSample={() => setJobDescription(SAMPLE_JOB_DESCRIPTION)}
                                currentLength={jobDescription.length}
                            />
                        </div>
                    </div>

                    {/* Bottom Panel: Generation Rules */}
                    <GenerationRules
                        rules={genConfig}
                        onChange={setGenConfig}
                    />

                    {/* Footer Actions */}
                    <div className="fixed bottom-0 left-0 right-0 p-6 bg-[#0c0e14]/80 backdrop-blur-md border-t border-white/5 z-50">
                        <div className="max-w-[1600px] mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
                            <div className="flex items-center gap-4 w-full md:w-auto">
                                <span className="text-sm font-medium text-gray-400 whitespace-nowrap hidden md:block">
                                    What would you like to generate?
                                </span>
                                <div className="flex p-1 rounded-lg bg-white/5 border border-white/5 w-full md:w-auto">
                                    <button
                                        onClick={() => setGenerationType('resume')}
                                        className={`
                                            flex items-center justify-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all flex-1 md:flex-none
                                            ${genConfig.document_type === 'resume'
                                                ? 'bg-white text-black shadow-lg shadow-white/10'
                                                : 'text-gray-400 hover:text-white hover:bg-white/5'
                                            }
                                        `}
                                    >
                                        <RiFileTextLine size={16} />
                                        Resume
                                    </button>
                                    <button
                                        onClick={() => setGenerationType('cover_letter')}
                                        className={`
                                            flex items-center justify-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all flex-1 md:flex-none
                                            ${genConfig.document_type === 'cover_letter'
                                                ? 'bg-white text-black shadow-lg shadow-white/10'
                                                : 'text-gray-400 hover:text-white hover:bg-white/5'
                                            }
                                        `}
                                    >
                                        <RiMailLine size={16} />
                                        Cover Letter
                                    </button>
                                </div>
                            </div>

                            <div className="flex items-center gap-4 w-full md:w-auto">
                                <GlassButton
                                    onClick={handleGenerate}
                                    className="!px-8 !py-2.5 !h-auto w-full md:w-auto flex items-center justify-center gap-2 group"
                                    gradient
                                    disabled={!selectedResumeId || isGenerating}
                                >
                                    {isGenerating ? (
                                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                    ) : (
                                        <>
                                            <RiMagicLine className="group-hover:rotate-12 transition-transform" />
                                            Generate with AI
                                        </>
                                    )}
                                </GlassButton>
                            </div>
                        </div>
                    </div>

                    <div className="h-24" />
                </>
            ) : (
                /* History View */
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div className="space-y-4">
                        <h2 className="text-xl font-bold text-white flex items-center gap-2">
                            <div className="w-8 h-8 rounded-lg bg-blue-500/20 text-blue-400 flex items-center justify-center">
                                <RiFileTextLine />
                            </div>
                            Generated Resumes
                        </h2>
                        <DocumentHistory
                            items={resumeHistory}
                            type="resume"
                            isLoading={historyLoading}
                        />
                    </div>

                    <div className="space-y-4">
                        <h2 className="text-xl font-bold text-white flex items-center gap-2">
                            <div className="w-8 h-8 rounded-lg bg-purple-500/20 text-purple-400 flex items-center justify-center">
                                <RiArticleLine />
                            </div>
                            Cover Letters
                        </h2>
                        <DocumentHistory
                            items={coverLetterHistory}
                            type="cover_letter"
                            isLoading={historyLoading}
                        />
                    </div>
                </div>
            )}
        </div>
    );
}
